import { FormEvent, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { TableColumn } from 'react-data-table-component';

import {
  AdminDeviceIntakeDeviceType,
  AdminDeviceIntakeHub,
  AdminDeviceIntakeTrap,
  IntakeToken,
} from 'api/traps/AdminDeviceIntake/types';
import FilterDataTable from 'components/FilterDataTable/FilterDataTable';
import Spinner from 'components/Common/Spinner';
import {
  useAdminDeviceIntake,
  useApproveAdminDeviceIntakeTrap,
  useRejectAdminDeviceIntakeTrap,
  useAssignIntakeToken,
  useCreateIntakeDevice,
  useIntakeTokenDevices,
} from 'hooks/Traps/AdminDeviceIntake/useAdminDeviceIntake';

type Device = AdminDeviceIntakeTrap | AdminDeviceIntakeHub;
const noop = () => undefined;
const DEVICE_SEARCH = ['serial_number', 'model', 'token', 'pest_company.name', 'object.name'];
const TOKEN_SEARCH = ['token'];
const errorMessage = (error: unknown) => {
  const body = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!body) return 'Request failed';
  return Object.entries(body)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join('; ');
};
const columns: TableColumn<Device>[] = [
  { name: 'ID', selector: r => r.id, sortable: true, sortField: 'id', width: '75px' },
  {
    name: 'Create date',
    selector: r => ('create_date' in r ? r.create_date || '-' : '-'),
    sortable: true,
    sortField: 'create_date',
  },
  { name: 'Serial number', selector: r => r.serial_number, sortable: true, sortField: 'serial_number' },
  { name: 'Model', selector: r => r.model, sortable: true, sortField: 'model' },
  {
    name: 'Pest company',
    selector: r => r.pest_company?.name || 'Unassigned',
    sortable: true,
    sortField: 'pest_company.name',
  },
  { name: 'Object', selector: r => r.object?.name || '-', sortable: true, sortField: 'object.name' },
  { name: 'Token', selector: r => r.token || '-', wrap: true },
  { name: 'Number', selector: r => r.sequence_number ?? '-', sortable: true, sortField: 'sequence_number' },
  {
    name: 'Battery',
    selector: r => ('battery_percentage' in r ? (r.battery_percentage ?? '-') : '-'),
    sortable: true,
    sortField: 'battery_percentage',
  },
  { name: 'Archived date', selector: r => r.archived_date || '-', sortable: true, sortField: 'archived_date' },
  { name: 'Archived', selector: r => (r.is_archived ? 'Yes' : 'No') },
];
function DeviceTable({
  title,
  devices,
  busy,
  selected,
  onSelect,
}: {
  title: string;
  devices: Device[];
  busy: boolean;
  selected?: number[];
  onSelect?: (ids: number[]) => void;
}) {
  const tableColumns: TableColumn<Device>[] =
    selected && onSelect
      ? [
          {
            name: '',
            width: '48px',
            cell: r => (
              <input
                type="checkbox"
                aria-label={`Select ${r.serial_number}`}
                disabled={busy}
                checked={selected.includes(r.id)}
                onChange={() =>
                  onSelect(selected.includes(r.id) ? selected.filter(id => id !== r.id) : [...selected, r.id])
                }
              />
            ),
          },
          ...columns.filter(c => ['Serial number', 'Model', 'Token'].includes(String(c.name))),
        ]
      : columns;
  return (
    <FilterDataTable
      columns={tableColumns}
      data={devices}
      title={title}
      isLoading={busy}
      searchFields={DEVICE_SEARCH}
      onChangePage={noop}
      onChangeFilterParams={noop}
      onChangeSearch={noop}
    />
  );
}
const AdminDeviceIntakePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [lookupInput, setLookupInput] = useState('');
  const [lookup, setLookup] = useState('');
  const { data, isLoading, isFetching, isError, error, refetch } = useAdminDeviceIntake(lookup);
  const approve = useApproveAdminDeviceIntakeTrap();
  const reject = useRejectAdminDeviceIntakeTrap();
  const assign = useAssignIntakeToken();
  const create = useCreateIntakeDevice();
  const [trapIds, setTrapIds] = useState<number[]>([]);
  const [hubIds, setHubIds] = useState<number[]>([]);
  const [targetToken, setTargetToken] = useState('');
  const [showAllTokens, setShowAllTokens] = useState(false);
  const [viewToken, setViewToken] = useState('');
  const tokenDevices = useIntakeTokenDevices(viewToken);
  const [showCreate, setShowCreate] = useState(false);
  const [deviceType, setDeviceType] = useState<AdminDeviceIntakeDeviceType>('traps');
  const [serial, setSerial] = useState('');
  const [modelId, setModelId] = useState('');
  const [createToken, setCreateToken] = useState('');
  const [createError, setCreateError] = useState('');
  const busy = approve.isLoading || reject.isLoading || assign.isLoading || create.isLoading;
  const selectedCount = trapIds.length + hubIds.length;
  const eligibleModels =
    data?.models.filter(m =>
      deviceType === 'hubs' ? m.model_type.toLowerCase() === 'hub' : m.model_type.toLowerCase() !== 'hub',
    ) || [];
  const showError = (err: unknown) => {
    enqueueSnackbar(errorMessage(err), { variant: 'error' });
  };
  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      enqueueSnackbar('Token copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Copy failed. Select and copy the token text.', { variant: 'error' });
    }
  };
  const handleAssign = () =>
    assign.mutate(
      { trap_ids: trapIds, hub_ids: hubIds, token: targetToken || undefined },
      {
        onSuccess: result => {
          setTrapIds([]);
          setHubIds([]);
          setTargetToken('');
          setViewToken(result.token);
          enqueueSnackbar(`${result.updated} devices assigned to token`, { variant: 'success' });
        },
        onError: showError,
      },
    );
  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    setCreateError('');
    create.mutate(
      {
        device_type: deviceType,
        serial_number: serial.trim(),
        model_id: Number(modelId),
        token: createToken && createToken !== '__new__' ? createToken : undefined,
        new_token: createToken === '__new__',
      },
      {
        onSuccess: result => {
          setShowCreate(false);
          setSerial('');
          setModelId('');
          setCreateToken('');
          enqueueSnackbar('Device created', { variant: 'success' });
          if (result.token) setViewToken(result.token);
        },
        onError: err => setCreateError(errorMessage(err)),
      },
    );
  };
  const tokenOptions = data?.tokens.map(t => (
    <option key={t.token} value={t.token}>
      {t.token} ({t.remaining} available)
    </option>
  ));
  const tokenColumns: TableColumn<IntakeToken>[] = [
    { name: 'Token', selector: r => r.token, wrap: true, grow: 3 },
    { name: 'Available traps', selector: r => r.remaining_traps, sortable: true, sortField: 'remaining_traps' },
    { name: 'Available hubs', selector: r => r.remaining_hubs, sortable: true, sortField: 'remaining_hubs' },
    { name: 'Claimed', selector: r => r.claimed, sortable: true, sortField: 'claimed' },
    { name: 'Status', selector: r => (r.active ? 'Active' : 'No available devices') },
    {
      name: 'Actions',
      minWidth: '160px',
      cell: r => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => setViewToken(r.token)}>
            Devices
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            aria-label={`Copy ${r.token}`}
            onClick={() => copyToken(r.token)}
          >
            Copy
          </button>
        </div>
      ),
    },
  ];
  return (
    <div className="page-content">
      <Container fluid>
        <div className="page-title-box d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 className="mb-0">Admin device intake</h3>
          <button className="btn btn-outline-secondary ms-auto" disabled={isFetching || busy} onClick={() => refetch()}>
            Refresh
          </button>
          <button
            className="btn btn-primary"
            disabled={!data || busy}
            onClick={() => {
              setCreateError('');
              setShowCreate(true);
            }}
          >
            Add device
          </button>
        </div>
        {isLoading && <Spinner />}
        {isError && (
          <div className="alert alert-danger" role="alert">
            {errorMessage(error)}
          </div>
        )}
        {data && (
          <>
            <div className="row mb-3">
              {[
                ['Pending traps', data.pending_traps.length],
                ['Stock traps', data.stock_traps.length],
                ['Stock hubs', data.stock_hubs.length],
                ['Active tokens', data.tokens.filter(t => t.active).length],
              ].map(([label, count]) => (
                <div className="col-md-3 mb-2" key={label}>
                  <div className="bg-white p-3">
                    <div className="text-muted">{label}</div>
                    <div className="fs-3 fw-bold">{count}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white p-3 mb-3">
              <h4>New pending traps</h4>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Serial number</th>
                      <th>Model</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending_traps.map(trap => (
                      <tr key={trap.id}>
                        <td>{trap.serial_number}</td>
                        <td>{trap.model}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-success me-2"
                            disabled={busy}
                            onClick={() =>
                              approve.mutate(trap.serial_number, {
                                onSuccess: () => {
                                  enqueueSnackbar('Trap approved', { variant: 'success' });
                                },
                                onError: showError,
                              })
                            }
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Delete pending trap ${trap.serial_number}?`))
                                reject.mutate(trap.serial_number, {
                                  onSuccess: () => {
                                    enqueueSnackbar('Trap rejected', { variant: 'success' });
                                  },
                                  onError: showError,
                                });
                            }}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!data.pending_traps.length && (
                      <tr>
                        <td colSpan={3} className="text-muted">
                          No pending traps
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white p-3 mb-3">
              <h4>Prepare devices for customer intake</h4>
              <p className="text-muted">
                Select stock devices and create a token or add them to an existing token. The customer uses the token to
                claim devices into their warehouse.
              </p>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>{selectedCount} selected</span>
                <select
                  className="form-select w-auto"
                  aria-label="Assignment token"
                  value={targetToken}
                  disabled={busy}
                  onChange={e => setTargetToken(e.target.value)}
                >
                  <option value="">Create a new token</option>
                  {tokenOptions}
                </select>
                <button className="btn btn-success" disabled={!selectedCount || busy} onClick={handleAssign}>
                  {assign.isLoading ? 'Saving…' : targetToken ? 'Add selected to token' : 'Create token for selected'}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  disabled={!selectedCount || busy}
                  onClick={() => {
                    setTrapIds([]);
                    setHubIds([]);
                  }}
                >
                  Clear selection
                </button>
              </div>
            </div>
            <div className="row">
              <div className="col-xl-6 mb-3 bg-white">
                <DeviceTable
                  title="Traps in Trojan stock"
                  devices={data.stock_traps}
                  busy={busy}
                  selected={trapIds}
                  onSelect={setTrapIds}
                />
              </div>
              <div className="col-xl-6 mb-3 bg-white">
                <DeviceTable
                  title="Hubs in Trojan stock"
                  devices={data.stock_hubs}
                  busy={busy}
                  selected={hubIds}
                  onSelect={setHubIds}
                />
              </div>
            </div>
            <div className="bg-white mb-3">
              <div className="px-3 pt-3 d-flex gap-2 align-items-center">
                <input
                  type="checkbox"
                  id="intake-all-tokens"
                  checked={showAllTokens}
                  onChange={e => setShowAllTokens(e.target.checked)}
                />
                <label htmlFor="intake-all-tokens">Show all tokens, including those with no available devices</label>
              </div>
              <FilterDataTable
                keyField="token"
                columns={tokenColumns}
                data={data.tokens.filter(t => showAllTokens || t.active)}
                title={showAllTokens ? 'All tokens' : 'Active tokens'}
                isLoading={busy}
                searchFields={TOKEN_SEARCH}
                onChangePage={noop}
                onChangeFilterParams={noop}
                onChangeSearch={noop}
              />
            </div>
            <div className="bg-white p-3 mb-3">
              <h4>Find a device and its owner</h4>
              <form
                className="d-flex gap-2 flex-wrap"
                onSubmit={e => {
                  e.preventDefault();
                  setLookup(lookupInput.trim());
                }}
              >
                <input
                  className="form-control flex-grow-1"
                  style={{ maxWidth: 600 }}
                  aria-label="Search all devices"
                  placeholder="Serial number, company, object, model or full token"
                  value={lookupInput}
                  onChange={e => setLookupInput(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  Search all devices
                </button>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setLookupInput('');
                    setLookup('');
                  }}
                >
                  Clear
                </button>
              </form>
              <div className="text-muted mt-2">
                {data.meta.matching_traps_count} traps · {data.meta.matching_hubs_count} hubs
              </div>
            </div>
            {(data.meta.all_traps_truncated || data.meta.all_hubs_truncated) && (
              <div className="alert alert-warning">
                Showing up to {data.meta.all_traps_limit} devices per type. Use “Search all devices” to search the
                entire inventory.
              </div>
            )}
            <div className="bg-white mb-3">
              <DeviceTable title="All traps" devices={data.all_traps} busy={isFetching} />
            </div>
            <div className="bg-white mb-3">
              <DeviceTable title="All hubs" devices={data.all_hubs} busy={isFetching} />
            </div>
          </>
        )}
        <Modal isOpen={showCreate} toggle={() => !create.isLoading && setShowCreate(false)}>
          <ModalHeader toggle={() => !create.isLoading && setShowCreate(false)}>Add device</ModalHeader>
          <ModalBody>
            <form onSubmit={handleCreate}>
              {createError && (
                <div className="alert alert-danger" role="alert">
                  {createError}
                </div>
              )}
              <label htmlFor="intake-device-type" className="form-label">
                Device type
              </label>
              <select
                id="intake-device-type"
                className="form-select mb-3"
                value={deviceType}
                disabled={create.isLoading}
                onChange={e => {
                  setDeviceType(e.target.value as AdminDeviceIntakeDeviceType);
                  setModelId('');
                }}
              >
                <option value="traps">Trap</option>
                <option value="hubs">Hub</option>
              </select>
              <label htmlFor="intake-serial" className="form-label">
                Serial number
              </label>
              <input
                id="intake-serial"
                className="form-control mb-3"
                required
                maxLength={50}
                value={serial}
                disabled={create.isLoading}
                onChange={e => setSerial(e.target.value)}
              />
              <label htmlFor="intake-model" className="form-label">
                Model
              </label>
              <select
                id="intake-model"
                className="form-select mb-3"
                required
                value={modelId}
                disabled={create.isLoading}
                onChange={e => setModelId(e.target.value)}
              >
                <option value="">Select model</option>
                {eligibleModels.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <label htmlFor="intake-create-token" className="form-label">
                Token
              </label>
              <select
                id="intake-create-token"
                className="form-select mb-3"
                value={createToken}
                disabled={create.isLoading}
                onChange={e => setCreateToken(e.target.value)}
              >
                <option value="">Assign later</option>
                <option value="__new__">Create a new token for this device</option>
                {tokenOptions}
              </select>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={create.isLoading || !serial.trim() || !modelId}
              >
                {create.isLoading ? 'Creating…' : 'Create device'}
              </button>
            </form>
          </ModalBody>
        </Modal>
        <Modal isOpen={!!viewToken} size="xl" toggle={() => setViewToken('')}>
          <ModalHeader toggle={() => setViewToken('')}>Token devices</ModalHeader>
          <ModalBody>
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <code>{viewToken}</code>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToken(viewToken)}>
                Copy token
              </button>
            </div>
            {tokenDevices.isLoading && <Spinner />}
            {tokenDevices.isError && (
              <div className="alert alert-danger" role="alert">
                {errorMessage(tokenDevices.error)}
              </div>
            )}
            {tokenDevices.data && (
              <>
                <DeviceTable title="Token traps" devices={tokenDevices.data.traps} busy={false} />
                <DeviceTable title="Token hubs" devices={tokenDevices.data.hubs} busy={false} />
              </>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};
export default AdminDeviceIntakePage;
